import React, { useEffect, useState, ReactNode } from 'react';
import './air.css';
import { loadYaml } from '../utilis/loadYAML';
import BifurcatedPromo from '../components/BifurcatedPromo';
import VideoComp from '../components/VideoComp';

// ✅ Individual icon imports (type-safe)
import { FaCrosshairs, FaRobot, FaBolt, FaChartLine, FaNetworkWired, FaLock } from 'react-icons/fa';

// ✅ TS-safe icon map with component references (no JSX here)
const iconMap: Record<string, any> = {
  'crosshair': FaCrosshairs,
  'autonomy': FaRobot,
  'bolt': FaBolt,
  'future': FaChartLine,
  'connectivity': FaNetworkWired,
  'shield': FaLock,
};

// ✅ Safe icon rendering via React.createElement
const getIcon = (iconName: string): React.ReactNode | null => {
  const IconComponent = iconMap[iconName];
  if (!IconComponent) {
    console.warn(`Icon not found for: ${iconName}`);
    return null;
  }
  return React.createElement(IconComponent, { className: 'feature-icon-svg' });
};

// Interfaces
interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Section {
  quote?: string;
  designation?: ReactNode;
  name?: ReactNode;
  type: string;
  id?: string;
  title?: string;
  description?: string;
  heading?: string;
  images?: string[];
  image?: string;
  video?: string;
  features?: Feature[];
}

function AIVision() {
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    loadYaml('/config/ai-vision.yaml')
      .then((data) => setSections(data.sections || []))
      .catch((err) => console.error('YAML load error:', err));
  }, []);

  return (
    <div className="mission-page">
      {sections.map((section, index) => {
        const renderHr = index !== 0 && section.type !== 'hero';

        // HERO SECTION
        if (section.type === 'hero' && section.image) {
          return (
            <section
              className="career-banner"
              style={{ backgroundImage: `url(${section.image})` }}
              key={index}
              id={section.id || `section-${index}`}
            >
              {section.title && <h1 className="career-banner-title">{section.title}</h1>}
            </section>
          );
        }

        // TEXT SECTION
        if (section.type === 'text') {
          const [boldLine, ...rest] = (section.description || '').split('---');
          const secondHalf = rest.join('---').trim();

          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="mission-section text-section"
                id={section.id || `section-${index}`}
              >
                <h2>{section.title}</h2>
                <div className="text-bifurcated-container">
                  <div className="text-bifurcated-left">
                    {boldLine && <p className="bold-line">{boldLine.trim()}</p>}
                  </div>
                  <div className="text-bifurcated-right">
                    {secondHalf && <p className="secondary-text">{secondHalf}</p>}
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        }

        // VIDEO PROMO SECTION
        if (section.type === 'videoPromo' && section.video) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <VideoComp
                id={section.id || `video-section-${index}`}
                title={section.title || ''}
                heading={section.heading || ''}
                description={section.description || ''}
                video={section.video}
              />
            </React.Fragment>
          );
        }

        // BIFURCATED PROMO SECTION
        if (section.type === 'bifurcatedPromo') {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <BifurcatedPromo
                id={section.id || `section-${index}`}
                title={section.title ?? ''}
                heading={section.heading ?? ''}
                description={section.description ?? ''}
                images={section.images ?? []}
              />
            </React.Fragment>
          );
        }

        // FEATURE GRID SECTION
        if (section.type === 'featureGrid' && section.features) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="section feature-grid-section"
                id={section.id || `section-${index}`}
              >
                {section.title && <h2 className="feature-grid-title">{section.title}</h2>}
                <div className="feature-grid">
                  {section.features.map((feature, i) => (
                    <div className="feature-box" key={i}>
                      <div className="feature-icon">{getIcon(feature.icon)}</div>
                      <h3 className="feature-title">{feature.title}</h3>
                      <p className="feature-description">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </React.Fragment>
          );
        }

        // TESTIMONY SECTION
        if (section.type === 'testimony') {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="mission-section text-section"
                id={section.id || `section-${index}`}
              >
                <div className="text-bifurcated-container">
                  <div className="text-bifurcated-left"></div>
                  <div className="text-bifurcated-right">
                    {section.quote && (
                      <p className="secondary-text" style={{ fontStyle: 'italic' }}>
                        "{section.quote}"
                      </p>
                    )}
                    {(section.name || section.designation) && (
                      <div className="testimonial-meta">
                        {section.name && <p className="testimonial-name">{section.name}</p>}
                        {section.designation && (
                          <p className="testimonial-designation">{section.designation}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        }

        return null;
      })}
    </div>
  );
}

export default AIVision;
